import { Box, Chip, FormControl, Grid, InputLabel, MenuItem, OutlinedInput, Select, TextField } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { FormikProvider } from "formik";
import { useIntl } from "react-intl";
import { IconDeviceFloppy, IconSquarePlus } from "@tabler/icons-react";
import ModalComponent from "components/Modal";
import type { CatalogosInventario, CampoInventario } from "../types/catalogoInventario.types";
import {
    COLORES_INVENTARIO,
    MENSAJE_SIN_OPCIONES,
    type CampoFormularioInventario,
} from "../constants/catalogoInventario.constants";
import type { useFormularioInventario } from "../hooks/useFormularioInventario";

interface Props {
    formulario: ReturnType<typeof useFormularioInventario>;
    catalogos: CatalogosInventario;
    guardando: boolean;
}

type Estilo = SystemStyleObject<Theme>;

/** Color verde de campo válido (variante outlined) */
const estiloColor = (color: string | undefined): Estilo =>
    color
        ? {
            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": { color },
            "& .MuiOutlinedInput-notchedOutline, & .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: `${color} !important`,
            },
            "& .MuiInputBase-input": { color },
        }
        : {};

/** Contenido del select con la misma altura y padding que un TextField outlined del tema */
const contenidoSelect: Estilo = {
    padding: "12px 36px 12px 12px !important",
    lineHeight: "1.4375em",
    minHeight: "1.4375em !important",
    height: "auto",
    display: "flex",
    alignItems: "center",
    textAlign: "left",
    fontSize: "0.875rem",
};

const iconoSelect: Estilo = {
    display: "inline-block !important",
    right: 10,
    color: "rgba(0, 0, 0, 0.54)",
};

const estiloSelect: Estilo = {
    "& .MuiInputBase-root": { minHeight: 44 },
    "& .MuiSelect-select": contenidoSelect,
    "& .MuiSelect-icon": iconoSelect,
};

/** El múltiple con chips usa menos padding vertical para no crecer */
const estiloSelectMultiple: Estilo = {
    "& .MuiInputBase-root": { minHeight: 44 },
    "& .MuiSelect-select": {
        ...contenidoSelect,
        paddingTop: "6px !important",
        paddingBottom: "6px !important",
        minHeight: "32px !important",
    },
    "& .MuiSelect-icon": iconoSelect,
};

export const FormularioInventarioModal = ({ formulario, catalogos, guardando }: Props): JSX.Element => {
    const intl = useIntl();
    const {
        formik,
        abierto,
        esEdicion,
        productoEditando,
        camposVisibles,
        proveedores,
        setProveedores,
        cerrar,
        colorCampo,
    } = formulario;

    const errorDe = (campo: CampoInventario) =>
        formik.touched[campo] && formik.errors[campo] ? formik.errors[campo] : undefined;

    const textoHeader = esEdicion
        ? `Editar producto: ${productoEditando?.nombre ?? ""}`
        : "Registrar producto";

    const tituloBoton = guardando
        ? `${intl.formatMessage({ id: esEdicion ? "general_editando" : "general_añadiendo" })}...`
        : intl.formatMessage({ id: esEdicion ? "general_editar" : "general_añadir" });

    const renderCampo = (c: CampoFormularioInventario) => {
        const etiqueta = `${intl.formatMessage({ id: c.etiquetaId })}${c.requerido ? " *" : ""}`;
        const opciones = c.origenOpciones ? catalogos[c.origenOpciones] : [];
        const sinOpciones = c.tipo === "seleccion" && opciones.length === 0;

        return (
            <Grid item xs={12} sm={c.columnas} key={c.nombre} sx={{ display: "flex", alignItems: "end" }}>
                <TextField
                    id={c.nombre}
                    name={c.nombre}
                    fullWidth
                    variant="outlined"
                    label={etiqueta}
                    select={c.tipo === "seleccion"}
                    disabled={sinOpciones || guardando}
                    inputMode={c.soloNumeros ? "numeric" : undefined}
                    value={formik.values[c.nombre]}
                    onChange={(e) => {
                        const valor = e.target.value;
                        if (c.soloNumeros === "entero" && valor !== "" && !/^\d+$/.test(valor)) return;
                        formik.setFieldValue(c.nombre, valor);
                    }}
                    onBlur={formik.handleBlur}
                    error={Boolean(errorDe(c.nombre))}
                    helperText={
                        sinOpciones && c.origenOpciones
                            ? intl.formatMessage({ id: MENSAJE_SIN_OPCIONES[c.origenOpciones] })
                            : errorDe(c.nombre)
                    }
                    sx={[estiloColor(colorCampo(c.nombre)), c.tipo === "seleccion" && estiloSelect]}
                >
                    {opciones.map((o) => (
                        <MenuItem key={o.id} value={String(o.id)}>
                            {o.nombre}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
        );
    };

    return (
        <ModalComponent
            handleClose={cerrar}
            isOpen={abierto}
            key="modal-formulario-inventario"
            procesando={guardando}
            textoHeader={textoHeader}
            actions={[
                {
                    title: tituloBoton,
                    icon: esEdicion ? <IconDeviceFloppy stroke={2} /> : <IconSquarePlus stroke={2} />,
                    color: COLORES_INVENTARIO.boton,
                    hoverColor: COLORES_INVENTARIO.botonHover,
                    textColor: "#fff",
                    disabledButton: guardando,
                    handleAction: () => formik.handleSubmit(),
                },
            ]}
        >
            <FormikProvider value={formik}>
                <Grid container spacing={2} sx={{ textAlign: "center", px: 1, pt: 2 }}>
                    {camposVisibles.map(renderCampo)}

                    {!esEdicion && (
                        <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "end" }}>
                            <FormControl fullWidth variant="outlined" disabled={guardando} sx={estiloSelectMultiple}>
                                <InputLabel id="proveedores-label">Proveedores</InputLabel>
                                <Select<number[]>
                                    labelId="proveedores-label"
                                    multiple
                                    value={proveedores}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setProveedores(typeof v === "string" ? v.split(",").map(Number) : v);
                                    }}
                                    input={<OutlinedInput label="Proveedores" />}
                                    renderValue={(sel) => (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                            {catalogos.proveedores
                                                .filter((p) => sel.includes(p.id))
                                                .map((p) => (
                                                    <Chip
                                                        key={p.id}
                                                        label={p.nombre}
                                                        size="small"
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onDelete={() => setProveedores(proveedores.filter((id) => id !== p.id))}
                                                        sx={{
                                                            backgroundColor: COLORES_INVENTARIO.boton,
                                                            color: "#fff",
                                                            "& .MuiChip-deleteIcon": { color: "#fff" },
                                                        }}
                                                    />
                                                ))}
                                        </Box>
                                    )}
                                >
                                    {catalogos.proveedores.map((p) => (
                                        <MenuItem
                                            key={p.id}
                                            value={p.id}
                                            sx={{ fontWeight: proveedores.includes(p.id) ? 600 : 400 }}
                                        >
                                            {p.nombre}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                </Grid>
            </FormikProvider>
        </ModalComponent>
    );
};
